import { useTranslation } from 'next-i18next/pages';

import {
  type SolutionResource,
  buildSolutionResourceDownloadPath,
} from '@/data/shared/solutionResources';

import { trackSolutionResourceDownload } from '@/utils/gtags';

import useCurrentLang from './useCurrentLang';

/**
 * ### SolutionResourceDownloadLink
 *
 * Everything a link to one resource needs, so the two screens that list
 * resources cannot drift apart in where they point or what they report.
 */
export type SolutionResourceDownloadLink = {
  /**
   * ### href
   *
   * Site-owned download path, already carrying the reader's language. It is
   * deliberately not run through the locale-aware link component: the language
   * is part of the path, and a second locale prefix would break it.
   */
  href: string;

  /**
   * ### onClick
   *
   * Reports the download. Safe to attach directly to the anchor; it never
   * prevents the default navigation, so a reader whose analytics are blocked
   * still gets the file.
   */
  onClick: () => void;

  /**
   * ### title
   *
   * Resource title translated into the reader's language.
   */
  title: string;
};

/**
 * ### useSolutionResourceDownload
 *
 * Resolves one resource against the language the reader is browsing in.
 *
 * @param resource Resource the link points at
 * @returns The link target, its title, and the click reporter
 */
export default function useSolutionResourceDownload(
  resource: SolutionResource,
): SolutionResourceDownloadLink {
  const { t } = useTranslation('common');
  const language = useCurrentLang();
  const title = t(resource.titleKey);

  return {
    href: buildSolutionResourceDownloadPath(resource, language),
    onClick: () => {
      trackSolutionResourceDownload({
        fileName: resource.fileName[language],
        fileTitle: title,
        language,
        resourceFormat: resource.format,
        resourceId: resource.id,
        solutionId: resource.product,
      });
    },
    title,
  };
}
